import { request } from "../../../services/api"
import type { BaseApiResponse } from "../../../types/api.ts"
import type { ResolvedWidget } from "../../DashboardPage/redux/widgets.types.ts"
import type {
  ReportExportFormat,
  ReportSchedule,
  ReportShareCandidate,
  ReportShareRoleCandidate,
  ReportSharesResponse,
  UpdateReportScheduleBody,
  UpdateReportSharesBody
} from "../types/collab.types.ts"
import type {
  ReportDetail,
  ReportFolderRelabelResult,
  ReportFolderRow,
  ReportListItem
} from "../types/types.ts"
import type {
  CreateReportData,
  GetReportsParams,
  ResolveReportWidgetBody,
  UpdateReportData
} from "./services.types.ts"

const BASE_URL = "/reports"
const RESOLVE_PATH = "/dashboard/widgets/resolve/"

export const getReports = (params?: GetReportsParams) =>
  request.api.get<ReportListItem[], BaseApiResponse<unknown>>(
    `${BASE_URL}/`,
    params
  )

// A folder with reports in it is only their `folder` label; these rows keep the
// caller's own empty folders alive. Rename and delete relabel, server-side and
// in one transaction, every report in the folder the caller may edit.
export const getReportFolders = () =>
  request.api.get<ReportFolderRow[], BaseApiResponse<unknown>>(
    `${BASE_URL}/folders/`
  )

export const createReportFolder = (name: string) =>
  request.api.post<BaseApiResponse<ReportFolderRow>, BaseApiResponse<unknown>>(
    `${BASE_URL}/folders/`,
    { name }
  )

export const renameReportFolder = (name: string, newName: string) =>
  request.api.post<
    BaseApiResponse<ReportFolderRelabelResult>,
    BaseApiResponse<unknown>
  >(`${BASE_URL}/folders/rename/`, { name, new_name: newName })

export const deleteReportFolder = (name: string) =>
  request.api.post<
    BaseApiResponse<ReportFolderRelabelResult>,
    BaseApiResponse<unknown>
  >(`${BASE_URL}/folders/delete/`, { name })

export const getReportDetails = (id: string) =>
  request.api.get<ReportDetail, BaseApiResponse<unknown>>(`${BASE_URL}/${id}/`)

export const createReport = (data: CreateReportData) =>
  request.api.post<BaseApiResponse<ReportDetail>, BaseApiResponse<unknown>>(
    `${BASE_URL}/`,
    data
  )

export const toggleReportFavorite = (id: string) =>
  request.api.post<
    BaseApiResponse<{ is_favorite: boolean }>,
    BaseApiResponse<unknown>
  >(`${BASE_URL}/${id}/favorite/`)

export const updateReport = (id: string, data: UpdateReportData) =>
  request.api.patch<BaseApiResponse<ReportDetail>, BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/`,
    data
  )

export const deleteReport = (id: string) =>
  request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/`
  )

export const postResolveWidget = (
  body: ResolveReportWidgetBody,
  params: Record<string, string>
) =>
  request.api.post<ResolvedWidget, BaseApiResponse<void>>(RESOLVE_PATH, body, {
    params
  })

export const getReportShares = (id: string) =>
  request.api.get<ReportSharesResponse, BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/shares/`
  )

export const getReportShareCandidates = (id: string) =>
  request.api.get<ReportShareCandidate[], BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/share-candidates/`
  )

export const getReportShareRoleCandidates = (id: string) =>
  request.api.get<ReportShareRoleCandidate[], BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/share-role-candidates/`
  )

export const updateReportShares = (id: string, body: UpdateReportSharesBody) =>
  request.api.put<
    BaseApiResponse<ReportSharesResponse>,
    BaseApiResponse<unknown>
  >(`${BASE_URL}/${id}/shares/`, body)

export const exportReport = (id: string, format: ReportExportFormat) =>
  request.api.get<Blob, BaseApiResponse<void>>(
    `${BASE_URL}/${id}/export/`,
    { format },
    { responseType: "blob" }
  )

export const getReportSchedule = (id: string) =>
  request.api.get<ReportSchedule | null, BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/schedule/`
  )

export const updateReportSchedule = (
  id: string,
  body: UpdateReportScheduleBody
) =>
  request.api.put<BaseApiResponse<ReportSchedule>, BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/schedule/`,
    body
  )

export const deleteReportSchedule = (id: string) =>
  request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    `${BASE_URL}/${id}/schedule/`
  )
